import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

function brl(v) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(v) || 0);
}

function KpiCard({ label, value, hint, tone = "default", testid }) {
  const toneCls = {
    default: "text-slate-900",
    emerald: "text-emerald-900",
    amber: "text-amber-700",
    rose: "text-rose-700",
  }[tone];
  return (
    <div className="card" data-testid={testid}>
      <div className="overline">{label}</div>
      <div className={`kpi-number text-4xl lg:text-5xl mt-2 ${toneCls}`}>{value}</div>
      {hint && <div className="text-xs text-slate-500 mt-2">{hint}</div>}
    </div>
  );
}

/**
 * Admin finance dashboard: MRR, total recebido, receita do mês, assinantes ativos,
 * churn, e tabela de últimas transações. Tudo agregado server-side.
 */
export default function FinanceiroAdmin() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/financeiro");
      setData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading && !data) {
    return (
      <div className="p-12 text-center text-slate-500">
        <i className="fa-solid fa-circle-notch fa-spin text-2xl mr-2"></i> Carregando indicadores…
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="p-8 lg:p-12 max-w-7xl mx-auto" data-testid="financeiro-page">
      <div className="flex items-baseline justify-between mb-8">
        <div>
          <div className="overline mb-2">Administração · Receita</div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight">Dashboard financeiro</h1>
          <p className="text-slate-600 mt-2 text-sm">
            Indicadores em tempo real baseados nas transações creditadas via Stripe.
          </p>
        </div>
        <button onClick={load} className="btn btn-secondary text-sm" data-testid="financeiro-reload">
          <i className="fa-solid fa-arrows-rotate"></i> Atualizar
        </button>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <KpiCard label="MRR" value={brl(data.mrr)} tone="emerald"
                 hint="Monthly Recurring Revenue · soma dos planos ativos normalizada a/mês"
                 testid="kpi-mrr" />
        <KpiCard label="Receita do mês" value={brl(data.month_paid)} tone="emerald"
                 hint="Pagamentos creditados no mês corrente"
                 testid="kpi-month-paid" />
        <KpiCard label="Recebido total" value={brl(data.total_paid_all_time)}
                 hint="Soma de todas as transações pagas"
                 testid="kpi-total" />
        <KpiCard label="Assinantes ativos" value={data.active_subscribers} tone="emerald"
                 hint={`${data.churned_this_month} churn(s) no mês passado`}
                 testid="kpi-active" />
      </div>

      {/* Active subscribers table */}
      <div className="card mb-6" data-testid="financeiro-active">
        <div className="flex justify-between items-baseline mb-3">
          <div className="overline">Assinantes ativos ({data.active_subscribers})</div>
          <span className="text-xs text-slate-500">paid_until {">"} hoje</span>
        </div>
        {data.active_users.length === 0 ? (
          <p className="text-slate-500 text-sm py-6 text-center">Nenhum assinante ativo no momento.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-slate-500 border-b border-slate-200">
              <tr>
                <th className="py-2 font-semibold uppercase text-xs">Usuário</th>
                <th className="font-semibold uppercase text-xs">Plano</th>
                <th className="font-semibold uppercase text-xs">Ciclo</th>
                <th className="font-semibold uppercase text-xs">Ativo desde</th>
                <th className="font-semibold uppercase text-xs">Vence em</th>
              </tr>
            </thead>
            <tbody>
              {data.active_users.map((u) => (
                <tr key={u.id} className="border-b border-slate-100">
                  <td className="py-2.5">
                    <div className="font-semibold text-slate-900">{u.name || "—"}</div>
                    <div className="text-xs text-slate-500 font-mono">{u.email}</div>
                  </td>
                  <td className="text-slate-700">{u.plan_label || u.plan_tier}</td>
                  <td>
                    <span className={`border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                      u.plan_cycle === "annual" ? "bg-emerald-50 text-emerald-900 border-emerald-300"
                                                : "bg-slate-50 text-slate-700 border-slate-300"}`}>
                      {u.plan_cycle === "annual" ? "Anual" : "Mensal"}
                    </span>
                  </td>
                  <td className="text-xs text-slate-500 font-mono">
                    {u.paid_since ? new Date(u.paid_since).toLocaleDateString("pt-BR") : "—"}
                  </td>
                  <td className="text-xs text-slate-500 font-mono">
                    {u.paid_until ? new Date(u.paid_until).toLocaleDateString("pt-BR") : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Recent transactions */}
      <div className="card" data-testid="financeiro-recent">
        <div className="overline mb-3">Últimas transações creditadas</div>
        {data.recent_transactions.length === 0 ? (
          <p className="text-slate-500 text-sm py-6 text-center">Nenhuma transação ainda.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-slate-500 border-b border-slate-200">
              <tr>
                <th className="py-2 font-semibold uppercase text-xs">Data</th>
                <th className="font-semibold uppercase text-xs">Cliente</th>
                <th className="font-semibold uppercase text-xs">Plano</th>
                <th className="font-semibold uppercase text-xs">Ciclo</th>
                <th className="font-semibold uppercase text-xs text-right">Valor</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_transactions.map((t) => (
                <tr key={t.session_id} className="border-b border-slate-100">
                  <td className="py-2.5 text-xs text-slate-500 font-mono">
                    {t.credited_at ? new Date(t.credited_at).toLocaleString("pt-BR") : "—"}
                  </td>
                  <td className="text-xs font-mono">{t.user_email || "—"}</td>
                  <td>{t.plan_label}</td>
                  <td className="text-xs uppercase tracking-wider text-slate-500">
                    {t.billing_cycle === "annual" ? "Anual" : "Mensal"}
                  </td>
                  <td className="text-right font-mono font-bold">{brl(t.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
